import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import { listGames, createGame, getLoungeChat, postLoungeChat, getAllTimeLeaderboard, getPeriodLeaderboard, getChatMessagesByTarget, deleteChatMessage } from '../api/gameApi';
import { useAuth } from '../context/AuthContext';
import ThinHeader from '../components/ThinHeader';
import UniversalChatDock from '../components/UniversalChatDock';
import useTurnNotifications from '../hooks/useTurnNotifications';
import {
  CHAT_SCOPE,
  getUserId,
  buildHomeLoungeTarget,
  buildRoomLoungeTarget,
  buildDmTarget,
  buildDmInboxTarget,
  toNormalizedMessage,
  messageMatchesTarget,
  buildOutgoingMessage,
  getPlaceholderForTarget,
  dedupeAndSortNormalizedMessages,
  computeUnreadByTarget,
} from '../utils/universalChat';

const HELP_CONTENT = {
  overview: {
    title: 'Get Started Fast',
    body: 'Join a room, play a round, and come back later when you want. Use Watch / Queue for active rooms, and Join for open lobby rooms. If you leave during an active round, a bot can finish the round in your seat.',
    guideAnchor: '#get-started',
  },
  createRoom: {
    title: 'Create Room Tips',
    body: 'Room names help players find your table. Add NPCs for open seats, then start once at least one human player is seated.',
    guideAnchor: '#create-room',
  },
  pwa: {
    title: 'P / W / A Meaning',
    body: 'P is Players seated, W is Waitlist, and A is Active users currently connected in that room.',
    guideAnchor: '#pwa',
  },
  tournaments: {
    title: 'Live vs Long-Play Tournaments',
    body: 'Live Tournament: everyone plays together in real time. An optional inactivity timer (10–60 min) automatically moves idle seated players back to the waiting room so active players can keep scoring — no one gets stuck waiting on someone who walked away. Long-Play Tournament: designed for weeks or months of play. Players post their moves when they can, like correspondence chess. No inactivity timer applies, so the table stays open indefinitely between turns.',
    guideAnchor: '#live-games',
  },
};

const getRoomActionMeta = (game) => {
  if (game.viewerIsSeated) {
    return { label: 'Return to Waiting Room', className: 'room-action-btn room-action-return' };
  }
  if (game.status === 'lobby') {
    return { label: 'Join', className: 'room-action-btn room-action-join' };
  }
  return { label: 'Watch / Queue', className: 'room-action-btn room-action-watch' };
};

const inferSocketUrl = () => {
  if (typeof window === 'undefined') return 'http://localhost:5030';

  if (process.env.REACT_APP_API_BASE) {
    return process.env.REACT_APP_API_BASE.replace(/\/api\/?$/, '');
  }

  const host = window.location.hostname.toLowerCase();
  if (host === 'frolickingelfstudio.ca' || host.endsWith('.frolickingelfstudio.ca')) {
    return 'https://api-necro.frolickingelfstudio.ca';
  }

  return process.env.NODE_ENV === 'production' ? window.location.origin : 'http://localhost:5030';
};

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || inferSocketUrl();

const HomePage = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [withDealer, setWithDealer] = useState(false);
  const [isPrivate, setIsPrivate] = useState(false);
  const [roomName, setRoomName] = useState('');
  const [dealerStrategy, setDealerStrategy] = useState('random');
  const [dealerDifficulty, setDealerDifficulty] = useState(3);
  const [tournamentEnabled, setTournamentEnabled] = useState(false);
  const [tournamentRecurrence, setTournamentRecurrence] = useState('monthly');
  const [tournamentLabel, setTournamentLabel] = useState('');
  const [tournamentMode, setTournamentMode] = useState('live');
  const [tournamentInactivityTimerEnabled, setTournamentInactivityTimerEnabled] = useState(false);
  const [tournamentInactivityTimeoutMinutes, setTournamentInactivityTimeoutMinutes] = useState(30);
  const [showHeaderLeaderboard, setShowHeaderLeaderboard] = useState(false);
  const [leaderboardMode, setLeaderboardMode] = useState('all-time');
  const [allTimeTopPlayers, setAllTimeTopPlayers] = useState([]);
  const [periodTopPlayers, setPeriodTopPlayers] = useState([]);
  const [periodMeta, setPeriodMeta] = useState(null);
  const [loungeMessages, setLoungeMessages] = useState([]);
  const [chatText, setChatText] = useState('');
  const [selectedRoomId, setSelectedRoomId] = useState('');
  const [selectedChatTargetId, setSelectedChatTargetId] = useState('home-lounge');
  const [roomChatState, setRoomChatState] = useState(null);
  const [selectedTargetHistoryMessages, setSelectedTargetHistoryMessages] = useState([]);
  const [lastSeenAtByTargetId, setLastSeenAtByTargetId] = useState({});
  const [homeChatCollapsed, setHomeChatCollapsed] = useState(true);
  const [createRoomExpanded, setCreateRoomExpanded] = useState(false);
  const [ownRoomsExpanded, setOwnRoomsExpanded] = useState(false);
  const [homeHelpOpen, setHomeHelpOpen] = useState(false);
  const [homeHelpSection, setHomeHelpSection] = useState('overview');
  const [alertsExpanded, setAlertsExpanded] = useState(false);
  const [alertsActionMessage, setAlertsActionMessage] = useState('');
  const prevTurnRoomsRef = useRef(new Set());
  const chatSocketRef = useRef(null);
  const {
    supported: notifSupported,
    enabled: notifEnabled,
    permission: notifPermission,
    mobileBrowser: notifMobileBrowser,
    standalone: notifStandalone,
    statusLabel: notifStatusLabel,
    setNotificationsEnabled,
    requestPermission,
    sendTurnNotification,
    triggerTestAlert,
  } = useTurnNotifications();

  const currentUserId = getUserId(user);
  const canModerateChat = ['admin', 'moderator', 'stickler'].includes(user?.role);
  const ownGames = games.filter((g) => {
    const gameHostId = `${g.hostId || g.host?._id || g.host?.id || g.createdBy || ''}`;
    return Boolean(currentUserId) && gameHostId === currentUserId;
  });

  const fetchLoungeChat = async () => {
    try {
      const { data } = await getLoungeChat(120);
      setLoungeMessages(data?.messages || []);
    } catch {
      // ignore
    }
  };

  const fetchGames = async () => {
    try {
      const { data } = await listGames();
      setGames(data);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  const fetchTopPlayers = useCallback(async () => {
    try {
      if (leaderboardMode === 'all-time') {
        const { data } = await getAllTimeLeaderboard(10);
        setAllTimeTopPlayers(data?.leaderboard || []);
        setPeriodMeta(null);
        return;
      }
      const { data } = await getPeriodLeaderboard({
        period: leaderboardMode,
        limit: 10,
        tournamentOnly: true,
      });
      setPeriodTopPlayers(data?.leaderboard || []);
      setPeriodMeta({
        periodLabel: data?.periodLabel || '',
        tournamentName: data?.tournamentName || 'Tournament',
        participantsTotal: Number(data?.participantsTotal || 0),
      });
    } catch {
      if (leaderboardMode === 'all-time') {
        setAllTimeTopPlayers([]);
      } else {
        setPeriodTopPlayers([]);
        setPeriodMeta(null);
      }
    }
  }, [leaderboardMode]);

  useEffect(() => {
    fetchGames();
    fetchLoungeChat();
    fetchTopPlayers();
    const gamesInterval = setInterval(fetchGames, 3500);
    const chatInterval = setInterval(fetchLoungeChat, 3000);
    const leaderboardInterval = setInterval(fetchTopPlayers, 12000);
    return () => {
      clearInterval(gamesInterval);
      clearInterval(chatInterval);
      clearInterval(leaderboardInterval);
    };
  }, [fetchTopPlayers]);

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (!token) return undefined;

    const socket = io(`${SOCKET_URL}/game`, {
      auth: { token },
      transports: ['websocket'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });
    chatSocketRef.current = socket;

    socket.on('connect', () => {
      if (selectedRoomId) {
        socket.emit('join-room', { gameId: selectedRoomId });
      }
    });

    socket.on('game-state', (state) => {
      if (!state?._id) return;
      if (selectedRoomId && `${state._id}` !== `${selectedRoomId}`) return;
      setRoomChatState(state);
    });

    socket.on('room-update', (state) => {
      if (!state?._id) return;
      if (selectedRoomId && `${state._id}` !== `${selectedRoomId}`) return;
      setRoomChatState(state);
    });

    return () => {
      socket.off('connect');
      socket.off('game-state');
      socket.off('room-update');
      socket.disconnect();
      chatSocketRef.current = null;
    };
  }, [selectedRoomId]);

  useEffect(() => {
    if (!selectedRoomId || !chatSocketRef.current) return;
    chatSocketRef.current.emit('join-room', { gameId: selectedRoomId });
  }, [selectedRoomId]);

  useEffect(() => {
    const turnRooms = games.filter((g) => g.viewerActionRequired && g.viewerActionType === 'your-turn');
    const currentIds = new Set(turnRooms.map((g) => String(g._id)));

    turnRooms.forEach((g) => {
      const key = String(g._id);
      if (prevTurnRoomsRef.current.has(key)) return;
      sendTurnNotification({
        title: 'Necronomicon: Your turn',
        body: `It is your turn in ${g.gameName || `Room ${g.roomCode}`}.`,
        tag: `turn-room-${key}`,
        targetPath: `/game/${key}/play`,
      });
    });

    prevTurnRoomsRef.current = currentIds;
  }, [games, sendTurnNotification]);

  const handleEnableTurnAlerts = async () => {
    setAlertsActionMessage('');
    setNotificationsEnabled(true);
    const result = await requestPermission();
    if (result === 'granted') {
      setAlertsActionMessage('Browser alert permission granted.');
    } else if (result === 'denied') {
      setAlertsActionMessage('Browser blocked alerts. Enable notifications in browser/site settings.');
    } else if (result === 'unsupported') {
      setAlertsActionMessage('This browser mode does not support native notifications.');
    }
  };

  const handleTestTurnAlert = () => {
    setAlertsActionMessage('');
    const ok = triggerTestAlert();
    if (!ok) {
      setAlertsActionMessage('No alert fired. Enable alerts or install to home screen first.');
    }
  };

  const handleTryBrowserNotifyNow = async () => {
    setAlertsActionMessage('');
    setNotificationsEnabled(true);
    const result = await requestPermission();
    if (result === 'granted') {
      const ok = triggerTestAlert();
      setAlertsActionMessage(ok ? 'Browser notification test sent.' : 'Permission granted, but no alert fired.');
      return;
    }
    if (result === 'denied') {
      setAlertsActionMessage('Notifications are blocked by browser settings for this site.');
      return;
    }
    if (result === 'unsupported') {
      setAlertsActionMessage('Native browser notifications are unavailable in this mode.');
      return;
    }
    setAlertsActionMessage('Notification permission not granted yet. Try Add to Home Screen on iPhone.');
  };

  const handleCreate = async () => {
    const trimmedRoomName = roomName.trim();
    if (!trimmedRoomName) {
      alert('Please name your room before creating it.');
      return;
    }
    try {
      const gameData = {
        gameName: trimmedRoomName,
        withDealer,
        isPrivate,
        dealerStrategy,
        dealerDifficulty,
        tournamentEnabled,
        tournamentRecurrence: tournamentEnabled ? tournamentRecurrence : 'none',
        tournamentLabel,
        tournamentMode: tournamentEnabled ? tournamentMode : 'live',
        tournamentInactivityTimerEnabled: tournamentEnabled ? tournamentInactivityTimerEnabled : false,
        tournamentInactivityTimeoutMinutes: tournamentEnabled ? tournamentInactivityTimeoutMinutes : 30,
      };
      const { data } = await createGame(gameData);
      navigate(`/game/${data.gameId}`);
    } catch (err) {
      alert(err.response?.data?.error || 'Could not create game');
    }
  };

  const handleSendChat = async (e) => {
    e.preventDefault();
    const text = chatText.trim();
    if (!text) return;

    const target = chatTargets.find((entry) => entry.id === selectedChatTargetId);
    if (!target) return;
    if (target.type === CHAT_SCOPE.DM_INBOX) return;

    const outgoing = buildOutgoingMessage(target, user, text);

    try {
      if (target.type === CHAT_SCOPE.LOUNGE || target.type === CHAT_SCOPE.DM) {
        await postLoungeChat(outgoing.body, outgoing.recipientId || undefined);
        await fetchLoungeChat();
      } else {
        if (!target.roomId || !chatSocketRef.current) {
          alert('Select a room before using room chat.');
          return;
        }
        chatSocketRef.current.emit('join-room', { gameId: target.roomId });
        chatSocketRef.current.emit('send-chat', {
          text: outgoing.body,
        });
      }
      setChatText('');
    } catch (err) {
      alert(err.response?.data?.error || 'Could not send lounge message');
    }
  };

  const openHelp = (section) => {
    setHomeHelpSection(section);
    setHomeHelpOpen(true);
  };

  const renderRoomCard = (g) => {
    const actionMeta = getRoomActionMeta(g);
    return (
      <li key={g._id}>
        <div className="game-room-summary">
          <div className="game-room-title-row">
            <span className={`game-activity-dot ${g.activeUsers > 0 ? 'active' : ''}`} aria-hidden="true" />
            <strong>{g.gameName || `Room ${g.roomCode}`}</strong>
            <span>({g.roomCode})</span>
          </div>
          <div className="game-room-status-row">
            {g.status}{g.isPrivate ? ' — Private' : ''}{g.isPersistent ? ' — Persistent' : ''}
            {g.tournament?.enabled
              ? ` — ${g.tournament?.label || 'Tournament'} (${g.tournament?.recurrence || 'none'}, ${g.tournament?.mode === 'long-play' ? 'long-play' : 'live'}${g.tournament?.inactivityTimerEnabled ? `, timer ${g.tournament?.inactivityTimeoutMinutes || 30}m` : ''})`
              : ''}
            {g.tournament?.enabled && (
              <button
                type="button"
                className="inline-help-btn"
                aria-label="Explain tournament modes"
                onClick={() => openHelp('tournaments')}
                style={{ marginLeft: '0.3rem' }}
              >
                ?
              </button>
            )}
          </div>
          <div className="game-room-stats-row" aria-label="Players waitlist active stats" title="P = Players seated, W = Waitlist, A = Active users connected">
            <span>P: {g.playerCount ?? g.players?.length ?? 0}</span>
            <span>W: {g.waitlistCount ?? 0}</span>
            <span>A: {g.activeUsers ?? 0}</span>
            <button
              type="button"
              className="inline-help-btn"
              aria-label="Explain room stats"
              onClick={() => openHelp('pwa')}
            >
              ?
            </button>
          </div>
          {g.viewerActionRequired && (
            <div className="game-room-stats-row" style={{ color: '#ffb36b', fontWeight: 700 }}>
              Action needed: {
                g.viewerActionType === 'swap-ready'
                  ? 'Mark Swap Ready'
                  : g.viewerActionType === 'your-turn'
                    ? 'Your Turn'
                    : g.viewerActionType === 'rematch-choice'
                      ? 'Play Again or Return to Waiting Room'
                      : 'Check Room'
              }
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: '0.45rem', flexWrap: 'wrap' }}>
          <button className={actionMeta.className} onClick={() => navigate(`/game/${g._id}`)}>
            {actionMeta.label}
          </button>
          {g.viewerActionRequired && (
            <button className="btn-play room-action-btn room-action-handle" onClick={() => navigate(`/game/${g._id}/play`)}>
              Handle Action
            </button>
          )}
        </div>
      </li>
    );
  };

  const activeHelp = HELP_CONTENT[homeHelpSection] || HELP_CONTENT.overview;
  const activeRoom = games.find((g) => g.viewerIsSeated || g.viewerActionRequired || g.viewerIsQueued) || null;
  const chatRoomOptions = useMemo(() => games.map((g) => ({
    id: `${g._id}`,
    label: `${g.gameName || `Room ${g.roomCode}`} (${g.roomCode})`,
    roomName: g.gameName || `Room ${g.roomCode}`,
    roomCode: g.roomCode,
  })), [games]);

  useEffect(() => {
    if (selectedRoomId) return;
    if (!activeRoom?._id) return;
    setSelectedRoomId(`${activeRoom._id}`);
    setSelectedChatTargetId(`room-game:${activeRoom._id}`);
  }, [activeRoom?._id, selectedRoomId]);

  const globalChatParticipantsMap = new Map();
  const upsertGlobalParticipant = ({ userId, displayName, isOnline = true }) => {
    const uid = `${userId || ''}`;
    if (!uid) return;
    if (!globalChatParticipantsMap.has(uid)) {
      globalChatParticipantsMap.set(uid, {
        userId: uid,
        displayName: displayName || 'Player',
        isOnline: !!isOnline,
      });
      return;
    }
    const existing = globalChatParticipantsMap.get(uid);
    globalChatParticipantsMap.set(uid, {
      ...existing,
      displayName: existing.displayName || displayName || 'Player',
      isOnline: existing.isOnline || !!isOnline,
    });
  };

  games.forEach((g) => {
    (g.players || []).forEach((p) => {
      if (p?.isBot) return;
      upsertGlobalParticipant({ userId: p?.userId, displayName: p?.displayName, isOnline: true });
    });
    (g.pendingJoinRequests || []).forEach((entry) => {
      upsertGlobalParticipant({ userId: entry?.userId, displayName: entry?.displayName, isOnline: true });
    });
    (g.queuedPlayers || []).forEach((entry) => {
      upsertGlobalParticipant({ userId: entry?.userId, displayName: entry?.displayName, isOnline: true });
    });
  });

  (loungeMessages || []).forEach((msg) => {
    upsertGlobalParticipant({ userId: msg?.userId, displayName: msg?.displayName, isOnline: true });
    upsertGlobalParticipant({ userId: msg?.recipientUserId, displayName: msg?.recipientDisplayName, isOnline: true });
  });

  const roomChatParticipantsMap = new Map();
  const upsertRoomParticipant = ({ userId, displayName, isOnline = true }) => {
    const uid = `${userId || ''}`;
    if (!uid) return;
    if (!roomChatParticipantsMap.has(uid)) {
      roomChatParticipantsMap.set(uid, {
        userId: uid,
        displayName: displayName || 'Player',
        isOnline: !!isOnline,
      });
      return;
    }
    const existing = roomChatParticipantsMap.get(uid);
    roomChatParticipantsMap.set(uid, {
      ...existing,
      displayName: existing.displayName || displayName || 'Player',
      isOnline: existing.isOnline || !!isOnline,
    });
  };

  (roomChatState?.chatParticipants || []).forEach((entry) => {
    upsertRoomParticipant({ userId: entry?.userId, displayName: entry?.displayName, isOnline: entry?.isOnline });
  });
  (roomChatState?.players || []).forEach((p) => {
    if (p?.isBot) return;
    upsertRoomParticipant({ userId: p?.userId, displayName: p?.displayName, isOnline: p?.isActiveInRoom });
  });
  (roomChatState?.pendingJoinRequests || []).forEach((entry) => {
    upsertRoomParticipant({ userId: entry?.userId, displayName: entry?.displayName, isOnline: true });
  });
  (roomChatState?.queuedPlayers || []).forEach((entry) => {
    upsertRoomParticipant({ userId: entry?.userId, displayName: entry?.displayName, isOnline: true });
  });
  (roomChatState?.loungeChatMessages || []).forEach((msg) => {
    upsertRoomParticipant({ userId: msg?.userId, displayName: msg?.displayName, isOnline: true });
    upsertRoomParticipant({ userId: msg?.recipientUserId, displayName: msg?.recipientDisplayName, isOnline: true });
  });
  (roomChatState?.gameChatMessages || []).forEach((msg) => {
    upsertRoomParticipant({ userId: msg?.userId, displayName: msg?.displayName, isOnline: true });
    upsertRoomParticipant({ userId: msg?.recipientUserId, displayName: msg?.recipientDisplayName, isOnline: true });
  });

  const selectedRoomSnapshot = games.find((g) => `${g._id}` === `${selectedRoomId || ''}`);
  const selectedRoomSnapshotParticipantsMap = new Map();
  const upsertSelectedRoomSnapshotParticipant = ({ userId, displayName, isOnline = true }) => {
    const uid = `${userId || ''}`;
    if (!uid) return;
    if (!selectedRoomSnapshotParticipantsMap.has(uid)) {
      selectedRoomSnapshotParticipantsMap.set(uid, {
        userId: uid,
        displayName: displayName || 'Player',
        isOnline: !!isOnline,
      });
      return;
    }
    const existing = selectedRoomSnapshotParticipantsMap.get(uid);
    selectedRoomSnapshotParticipantsMap.set(uid, {
      ...existing,
      displayName: existing.displayName || displayName || 'Player',
      isOnline: existing.isOnline || !!isOnline,
    });
  };

  (selectedRoomSnapshot?.players || []).forEach((p) => {
    if (p?.isBot) return;
    upsertSelectedRoomSnapshotParticipant({ userId: p?.userId, displayName: p?.displayName, isOnline: true });
  });
  (selectedRoomSnapshot?.pendingJoinRequests || []).forEach((entry) => {
    upsertSelectedRoomSnapshotParticipant({ userId: entry?.userId, displayName: entry?.displayName, isOnline: true });
  });
  (selectedRoomSnapshot?.queuedPlayers || []).forEach((entry) => {
    upsertSelectedRoomSnapshotParticipant({ userId: entry?.userId, displayName: entry?.displayName, isOnline: true });
  });

  const roomChatParticipants = Array.from(roomChatParticipantsMap.values()).sort((a, b) => a.displayName.localeCompare(b.displayName));
  const selectedRoomSnapshotParticipants = Array.from(selectedRoomSnapshotParticipantsMap.values()).sort((a, b) => a.displayName.localeCompare(b.displayName));
  const homeChatParticipants = Array.from(globalChatParticipantsMap.values()).sort((a, b) => a.displayName.localeCompare(b.displayName));

  const participantsMap = new Map();
  [...homeChatParticipants, ...roomChatParticipants, ...selectedRoomSnapshotParticipants].forEach((entry) => {
    const uid = `${entry?.userId || ''}`;
    if (!uid || uid === currentUserId) return;
    if (!participantsMap.has(uid)) {
      participantsMap.set(uid, {
        userId: uid,
        displayName: entry.displayName || 'Player',
        isOnline: !!entry.isOnline,
      });
      return;
    }
    const existing = participantsMap.get(uid);
    participantsMap.set(uid, {
      ...existing,
      isOnline: existing.isOnline || !!entry.isOnline,
      displayName: existing.displayName || entry.displayName || 'Player',
    });
  });

  const dmTargets = Array.from(participantsMap.values())
    .sort((a, b) => a.displayName.localeCompare(b.displayName))
    .map((entry) => buildDmTarget(entry));

  const selectedRoomOption = chatRoomOptions.find((room) => `${room.id}` === `${selectedRoomId || ''}`) || null;

  const roomTargets = selectedRoomOption
    ? ([
      buildRoomLoungeTarget({
        roomId: selectedRoomOption.id,
        roomName: selectedRoomOption.roomName,
        roomCode: selectedRoomOption.roomCode,
      }),
    ])
    : [];

  const chatTargets = [buildHomeLoungeTarget(), ...roomTargets, buildDmInboxTarget(), ...dmTargets];
  const effectiveSelectedChatTargetId = chatTargets.some((entry) => entry.id === selectedChatTargetId)
    ? selectedChatTargetId
    : (chatTargets[0]?.id || 'home-lounge');
  const selectedChatTarget = chatTargets.find((entry) => entry.id === effectiveSelectedChatTargetId) || chatTargets[0];

  const homeNormalizedMessages = (loungeMessages || [])
    .map((msg) => toNormalizedMessage({ message: msg, fallbackScope: CHAT_SCOPE.LOUNGE }));

  const roomLoungeNormalizedMessages = (roomChatState?.loungeChatMessages || [])
    .map((msg) => toNormalizedMessage({
      message: msg,
      fallbackScope: CHAT_SCOPE.ROOM_GAME,
      roomId: roomChatState?._id,
    }));

  const roomGameNormalizedMessages = (roomChatState?.gameChatMessages || [])
    .map((msg) => toNormalizedMessage({
      message: msg,
      fallbackScope: CHAT_SCOPE.ROOM_GAME,
      roomId: roomChatState?._id,
    }));

  const normalizedMessages = dedupeAndSortNormalizedMessages([
    ...homeNormalizedMessages,
    ...roomLoungeNormalizedMessages,
    ...roomGameNormalizedMessages,
  ]).filter(Boolean);

  const selectedTargetHistoryNormalized = dedupeAndSortNormalizedMessages(
    (selectedTargetHistoryMessages || []).map((msg) => toNormalizedMessage({
      message: msg,
      fallbackScope: selectedChatTarget?.type || CHAT_SCOPE.LOUNGE,
      roomId: selectedChatTarget?.roomId || null,
    }))
  ).filter(Boolean);

  const visibleMessages = dedupeAndSortNormalizedMessages([
    ...normalizedMessages,
    ...selectedTargetHistoryNormalized,
  ]).filter((msg) => messageMatchesTarget(msg, selectedChatTarget, currentUserId));

  const unread = computeUnreadByTarget({
    targets: chatTargets,
    messages: dedupeAndSortNormalizedMessages([
      ...normalizedMessages,
      ...selectedTargetHistoryNormalized,
    ]),
    currentUserId,
    lastSeenAtByTargetId,
  });
  const unreadByTargetId = {
    ...unread.unreadByTargetId,
    [selectedChatTarget?.id || '']: 0,
  };
  const totalUnread = Object.values(unreadByTargetId).reduce((sum, value) => sum + Number(value || 0), 0);
  const unreadNotices = unread.notices.filter((entry) => entry.targetId !== selectedChatTarget?.id);

  const selectedTargetParticipants = selectedChatTarget?.type === CHAT_SCOPE.DM
    ? Array.from(participantsMap.values()).filter((entry) => `${entry.userId}` === `${selectedChatTarget.recipientId || ''}`)
    : Array.from(participantsMap.values());

  const markTargetSeen = (targetId) => {
    if (!targetId) return;
    setLastSeenAtByTargetId((prev) => ({
      ...prev,
      [targetId]: new Date().toISOString(),
    }));
  };

  const handleSelectTarget = (targetId) => {
    setSelectedChatTargetId(targetId);
    markTargetSeen(targetId);
  };

  const removeMessageById = (entry, messageId) => `${entry?.messageId || entry?._id || entry?.id || ''}` !== `${messageId || ''}`;

  const handleDeleteChatMessage = async (msg) => {
    const messageId = `${msg?.id || ''}`;
    if (!messageId) return;

    try {
      const effectiveScope = msg?.scope || selectedChatTarget?.type || '';
      const targetRoomId = (effectiveScope === CHAT_SCOPE.ROOM_LOUNGE || effectiveScope === CHAT_SCOPE.ROOM_GAME || effectiveScope === CHAT_SCOPE.DM)
        ? (msg?.roomId || selectedRoomId || undefined)
        : undefined;

      await deleteChatMessage(messageId, {
        scope: effectiveScope,
        roomId: targetRoomId,
      });

      setLoungeMessages((prev) => prev.filter((entry) => removeMessageById(entry, messageId)));
      setSelectedTargetHistoryMessages((prev) => prev.filter((entry) => removeMessageById(entry, messageId)));
      setRoomChatState((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          loungeChatMessages: (prev.loungeChatMessages || []).filter((entry) => removeMessageById(entry, messageId)),
          gameChatMessages: (prev.gameChatMessages || []).filter((entry) => removeMessageById(entry, messageId)),
          chatMessages: (prev.chatMessages || []).filter((entry) => removeMessageById(entry, messageId)),
        };
      });
    } catch (err) {
      alert(err.response?.data?.error || 'Could not delete chat message');
    }
  };

  const selectedTargetRoomId = selectedChatTarget?.roomId || '';

  useEffect(() => {
    if (selectedTargetRoomId && selectedTargetRoomId !== selectedRoomId) {
      setSelectedRoomId(selectedTargetRoomId);
    }
  }, [selectedRoomId, selectedTargetRoomId]);

  useEffect(() => {
    if (selectedChatTarget?.type === CHAT_SCOPE.DM_INBOX) {
      setSelectedTargetHistoryMessages([]);
      return;
    }

    const target = {
      type: selectedChatTarget?.type,
      roomId: selectedChatTarget?.roomId,
      recipientId: selectedChatTarget?.recipientId,
    };
    if (!target.type) return;

    let cancelled = false;
    const loadTargetHistory = async () => {
      try {
        const { data } = await getChatMessagesByTarget({
          scope: target.type,
          roomId: target.roomId || undefined,
          recipientId: target.recipientId || undefined,
          limit: 160,
        });
        if (cancelled) return;
        setSelectedTargetHistoryMessages(data?.messages || []);
      } catch {
        if (cancelled) return;
        setSelectedTargetHistoryMessages([]);
      }
    };

    loadTargetHistory();
    return () => {
      cancelled = true;
    };
  }, [selectedChatTarget?.type, selectedChatTarget?.roomId, selectedChatTarget?.recipientId]);

  return (
    <div className="home-page">
      <ThinHeader
        homeTo="/"
        onToggleLeaderboard={() => setShowHeaderLeaderboard((prev) => !prev)}
        leaderboardOpen={showHeaderLeaderboard}
        userLabel={user?.displayName || user?.username || user?.email || 'Player'}
        showAdminLink={user?.role === 'admin'}
      />

      {showHeaderLeaderboard && (
        <div className="header-leaderboard-panel">
          <h3>Top 10 Players</h3>
          <div className="leaderboard-actions-row">
            <button type="button" className="thin-header-btn" onClick={() => setLeaderboardMode('all-time')}>
              All-Time
            </button>
            <button type="button" className="thin-header-btn" onClick={() => setLeaderboardMode('monthly')}>
              Monthly Tournament
            </button>
            <button type="button" className="thin-header-btn" onClick={() => setLeaderboardMode('weekly')}>
              Weekly Tournament
            </button>
          </div>
          {leaderboardMode !== 'all-time' && periodMeta && (
            <p className="admin-note" style={{ marginBottom: '0.55rem' }}>
              {periodMeta.periodLabel || (leaderboardMode === 'monthly' ? 'Monthly period' : 'Weekly period')}
              {' '}• {periodMeta.tournamentName || 'Tournament'}
              {' '}• {periodMeta.participantsTotal || 0} participants
            </p>
          )}
          {(leaderboardMode === 'all-time' ? allTimeTopPlayers : periodTopPlayers).length > 0 ? (
            <ul>
              {(leaderboardMode === 'all-time' ? allTimeTopPlayers : periodTopPlayers).map((p, idx) => (
                <li key={p.userId || idx}>
                  <span className="leader-rank">#{idx + 1}</span>
                  <span className="leader-name">{p.displayName}</span>
                  <span className="leader-metrics">
                    {p.totalPoints ?? 0} pts • {p.roundsWon ?? p.gamesWon ?? 0} wins
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="header-leaderboard-empty">
              {leaderboardMode === 'all-time' ? 'No all-time stats yet.' : 'No tournament stats for this period yet.'}
            </p>
          )}
        </div>
      )}

      <header>
        <h1>Lounge</h1>
        <span>Welcome, {user?.displayName}</span>
        <button onClick={signOut}>Logout</button>
      </header>

      <section className="turn-alerts-panel" aria-label="Turn alerts">
        <div className="turn-alerts-panel-head">
          <h2>Alerts</h2>
          <div className="turn-alerts-actions turn-alerts-actions--header">
            <button
              type="button"
              className="thin-header-btn"
              onClick={() => setAlertsExpanded((prev) => !prev)}
              aria-expanded={alertsExpanded}
            >
              {alertsExpanded ? 'Collapse' : 'Expand'}
            </button>
          </div>
        </div>

        {alertsExpanded && (
          <>
            <div className="turn-alerts-actions">
              {notifSupported && (
                <button type="button" className="thin-header-btn" onClick={handleEnableTurnAlerts}>
                  {notifPermission === 'granted' ? 'Browser Alerts Enabled' : 'Enable Browser Alerts'}
                </button>
              )}
              <button
                type="button"
                className="thin-header-btn"
                onClick={() => setNotificationsEnabled(!notifEnabled)}
              >
                {notifEnabled ? 'Turn Alerts: ON' : 'Turn Alerts: OFF'}
              </button>
              <button type="button" className="thin-header-btn" onClick={handleTryBrowserNotifyNow}>
                Try Browser Notify Now
              </button>
              <button type="button" className="thin-header-btn" onClick={handleTestTurnAlert}>
                Test Alert
              </button>
            </div>
            <p className="admin-note">{notifStatusLabel}</p>
            {alertsActionMessage && <p className="admin-note">{alertsActionMessage}</p>}
            {notifMobileBrowser && !notifStandalone && notifPermission !== 'granted' && (
              <p className="admin-note">Tip: On iPhone Safari, open Share menu -> Add to Home Screen, then open Necronomicon from the icon and tap Try Browser Notify Now.</p>
            )}
          </>
        )}
      </section>

      <section className="home-get-started" aria-label="Get started">
        <div className="home-get-started-head">
          <h2>Get Started</h2>
          <button type="button" className="inline-help-btn" aria-label="How to get started" onClick={() => openHelp('overview')}>?</button>
        </div>
        <p>Join an active room, talk with friends, and jump into live games.</p>
        <ul>
          <li>Use Join for open lobby rooms.</li>
          <li>Use Watch / Queue to follow live games or long-turn games.</li>
          <li>Name rooms clearly for events and tournaments.</li>
          <li>Need bot help? Create with NPC dealer, then adjust difficulty.</li>
        </ul>
        <button type="button" className="thin-header-btn" onClick={() => navigate('/guide#get-started')}>Open Full Guide</button>
      </section>

      <UniversalChatDock
        collapsed={homeChatCollapsed}
        onToggleCollapsed={() => setHomeChatCollapsed((prev) => !prev)}
        targets={chatTargets}
        selectedTargetId={effectiveSelectedChatTargetId}
        onSelectTarget={handleSelectTarget}
        participants={selectedTargetParticipants}
        messages={visibleMessages}
        currentUserId={currentUserId}
        inputValue={chatText}
        onInputChange={setChatText}
        onSend={handleSendChat}
        placeholder={getPlaceholderForTarget(selectedChatTarget)}
        unreadCount={totalUnread}
        unreadByTargetId={unreadByTargetId}
        unreadNotices={unreadNotices}
        onNoticeClick={handleSelectTarget}
        onReplyToSender={(msg) => {
          if (!msg?.senderId) return;
          handleSelectTarget(`dm:${msg.senderId}`);
        }}
        canDeleteMessages={canModerateChat}
        onDeleteMessage={handleDeleteChatMessage}
        emptyState="No messages yet for this target."
      />

      <section className={`create-game section-card ${createRoomExpanded ? 'expanded' : 'collapsed'}`}>
        <div className="section-header-row">
          <h2>
            Create Room
            <button type="button" className="inline-help-btn" aria-label="Create room help" onClick={() => openHelp('createRoom')}>?</button>
          </h2>
          <button type="button" className="section-toggle-btn" onClick={() => setCreateRoomExpanded((prev) => !prev)}>
            {createRoomExpanded ? 'Collapse' : 'Expand'}
          </button>
        </div>
        {createRoomExpanded && (
          <div className="section-content">
            <p>Table size is fixed at 9 players.</p>
            <label className="create-field-label create-field-label--stacked">
              Room Name:
              <input
                type="text"
                maxLength={60}
                value={roomName}
                onChange={(e) => setRoomName(e.target.value)}
                placeholder="Friday Night Throwdown"
              />
            </label>
            <label className="create-field-label">
              <input
                type="checkbox"
                checked={withDealer}
                onChange={(e) => setWithDealer(e.target.checked)}
              />
              Play with NPC Dealer
            </label>
            <label className="create-field-label">
              <input
                type="checkbox"
                checked={isPrivate}
                onChange={(e) => setIsPrivate(e.target.checked)}
              />
              Private game (block join/seat requests)
            </label>
            <label className="create-field-label">
              <input
                type="checkbox"
                checked={tournamentEnabled}
                onChange={(e) => setTournamentEnabled(e.target.checked)}
              />
              Tournament room
            </label>
            {tournamentEnabled && (
              <>
                <label className="create-field-label create-field-label--stacked">
                  Tournament Recurrence:
                  <select value={tournamentRecurrence} onChange={(e) => setTournamentRecurrence(e.target.value)}>
                    <option value="monthly">Monthly</option>
                    <option value="weekly">Weekly</option>
                    <option value="none">One-Time</option>
                  </select>
                </label>
                <label className="create-field-label create-field-label--stacked">
                  Tournament Label (optional):
                  <input
                    type="text"
                    maxLength={80}
                    value={tournamentLabel}
                    onChange={(e) => setTournamentLabel(e.target.value)}
                    placeholder="Spring Invitational"
                  />
                </label>
                <label className="create-field-label create-field-label--stacked">
                  Tournament Mode:
                  <select value={tournamentMode} onChange={(e) => setTournamentMode(e.target.value)}>
                    <option value="live">Live Tournament</option>
                    <option value="long-play">Long-Play Tournament</option>
                  </select>
                </label>
                <label className="create-field-label">
                  <input
                    type="checkbox"
                    checked={tournamentInactivityTimerEnabled}
                    disabled={tournamentMode !== 'live'}
                    onChange={(e) => setTournamentInactivityTimerEnabled(e.target.checked)}
                  />
                  Inactivity timer (live tournaments only)
                </label>
                {tournamentMode === 'live' && tournamentInactivityTimerEnabled && (
                  <label className="create-field-label create-field-label--stacked">
                    Inactivity timeout:
                    <select
                      value={tournamentInactivityTimeoutMinutes}
                      onChange={(e) => setTournamentInactivityTimeoutMinutes(Number(e.target.value))}
                    >
                      <option value={10}>10 minutes</option>
                      <option value={15}>15 minutes</option>
                      <option value={20}>20 minutes</option>
                      <option value={30}>30 minutes</option>
                      <option value={60}>1 hour</option>
                    </select>
                  </label>
                )}
              </>
            )}
            {withDealer && (
              <>
                <label className="create-field-label create-field-label--stacked">
                  Dealer Strategy:
                  <select value={dealerStrategy} onChange={(e) => setDealerStrategy(e.target.value)}>
                    <option value="random">Random</option>
                    <option value="aggressive">Aggressive</option>
                    <option value="passive">Passive</option>
                  </select>
                </label>
                <label className="create-field-label create-field-label--stacked">
                  Difficulty (1-5):
                  <input
                    type="number"
                    min="1"
                    max="5"
                    value={dealerDifficulty}
                    onChange={(e) => setDealerDifficulty(Number(e.target.value))}
                  />
                </label>
              </>
            )}
            <button className="btn-play room-action-btn room-action-create" onClick={handleCreate} style={{ marginTop: '10px' }}>Create Game</button>
          </div>
        )}
      </section>

      <section className={`open-games section-card ${ownRoomsExpanded ? 'expanded' : 'collapsed'}`}>
        <div className="section-header-row">
          <h2>Own Rooms</h2>
          <button type="button" className="section-toggle-btn" onClick={() => setOwnRoomsExpanded((prev) => !prev)}>
            {ownRoomsExpanded ? 'Collapse' : 'Expand'}
          </button>
        </div>
        {ownRoomsExpanded && (
          <div className="section-content">
            {ownGames.length === 0 ? (
              <p>You are not hosting any rooms right now. Create one and invite your crew.</p>
            ) : (
              <ul>{ownGames.map(renderRoomCard)}</ul>
            )}
          </div>
        )}
      </section>

      <section className="open-games">
        <h2>
          Open/Active Games
          <button type="button" className="inline-help-btn" aria-label="Explain room stats" onClick={() => openHelp('pwa')}>?</button>
          <button type="button" className="refresh-btn" onClick={fetchGames}>↻</button>
        </h2>
        {loading ? (
          <p>Loading...</p>
        ) : games.length === 0 ? (
          <p>No open or active games. Create one!</p>
        ) : (
          <ul>{games.map(renderRoomCard)}</ul>
        )}
      </section>

      {homeHelpOpen && (
        <div className="help-modal-overlay" role="dialog" aria-modal="true" aria-label="Home help modal">
          <div className="help-modal-panel">
            <h3>{activeHelp.title}</h3>
            <p>{activeHelp.body}</p>
            <div className="help-modal-actions">
              <button
                type="button"
                className="thin-header-btn"
                onClick={() => {
                  setHomeHelpOpen(false);
                  navigate(`/guide${activeHelp.guideAnchor}`);
                }}
              >
                Open Full Guide
              </button>
              <button type="button" className="btn-cancel" onClick={() => setHomeHelpOpen(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;
